import {DeepPartial} from 'utility-types';

import {defaultTo} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {GetDirectSaleVariantResponse} from '@dms/api/warehouse';

import {Nullish} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../types/WarehouseDirectSaleVariantsDetailFormSchema';
import {INTERNAL_AUTO_INITIATE_PAYMENT} from './buildPaymentDocumentsPatchBody';

type TemplateIdResolver = (templateId: string | Nullish) => string | undefined;

export interface InvoiceTemplateResolvers {
  resolveStandardInvoiceTemplateId: TemplateIdResolver;
  resolveInternalInvoiceTemplateId: TemplateIdResolver;
}

type PaymentDocumentsDefaultValues = Pick<
  NonNullable<DeepPartial<WarehouseDirectSaleVariantsDetailFormSchemaType>['documents']>,
  'cashPayment' | 'cashOnDeliveryPayment' | 'cardPayment' | 'bankPayment' | 'internalPayment'
>;

// A disabled payment type is passed through untouched: its invoice node is submitted as stored, so
// healing it would rewrite a configuration the operator cannot even see.
const withInvoiceTemplateId = <
  T extends {isEnable?: boolean | null; invoice?: {templateId?: string | null} | null},
>(
  payment: T | Nullish,
  resolveTemplateId: TemplateIdResolver
) => {
  const isEnable = defaultTo(false, payment?.isEnable);

  return isTrue(isEnable)
    ? {
        ...payment,
        isEnable,
        invoice: {
          ...payment?.invoice,
          templateId: resolveTemplateId(payment?.invoice?.templateId),
        },
      }
    : {...payment, isEnable};
};

export function buildPaymentDocumentsDefaultValues(
  documents: GetDirectSaleVariantResponse['documents'] | Nullish,
  resolvers: InvoiceTemplateResolvers
): PaymentDocumentsDefaultValues {
  return {
    cashPayment: {
      ...withInvoiceTemplateId(documents?.cashPayment, resolvers.resolveStandardInvoiceTemplateId),
      isAutoInitiatePayment: defaultTo(false, documents?.cashPayment?.isAutoInitiatePayment),
    },
    cashOnDeliveryPayment: withInvoiceTemplateId(
      documents?.cashOnDeliveryPayment,
      resolvers.resolveStandardInvoiceTemplateId
    ),
    cardPayment: {
      ...withInvoiceTemplateId(documents?.cardPayment, resolvers.resolveStandardInvoiceTemplateId),
      isAutoInitiatePayment: defaultTo(false, documents?.cardPayment?.isAutoInitiatePayment),
    },
    bankPayment: withInvoiceTemplateId(
      documents?.bankPayment,
      resolvers.resolveStandardInvoiceTemplateId
    ),
    // The stored value is intentionally overridden, matching the save path — free of charge is
    // always initiated automatically and its checkbox is read-only.
    internalPayment: {
      ...withInvoiceTemplateId(
        documents?.internalPayment,
        resolvers.resolveInternalInvoiceTemplateId
      ),
      isAutoInitiatePayment: INTERNAL_AUTO_INITIATE_PAYMENT,
    },
  };
}
